import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TagBox from 'devextreme-react/tag-box';
import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { showErrorToast, showSuccessToast } from '@jarvis/platform-ui';
import {
  createAdminCategory,
  createIngestBatch,
  createMediaTag,
  fetchAdminCategories,
  fetchAdminTags,
  fetchIngestBatch,
  uploadIngestFiles,
  type MediaCategoryAdmin,
  type MediaIngestBatch,
  type MediaLibraryMode,
  type MediaTagAdmin
} from '../../../api/mediaLibrary';
import { ToolNotice } from '../../../apps/shared/ToolNotice';

interface MediaLibraryIngestPopupProps {
  open: boolean;
  library: Exclude<MediaLibraryMode, 'uploads'>;
  onClose: () => void;
  onCompleted: () => void;
}

const TERMINAL_BATCH_STATES = new Set(['Completed', 'Failed', 'Cancelled']);

function isBatchTerminal(state: string): boolean {
  return TERMINAL_BATCH_STATES.has(state);
}

function toastBatchResult(batch: MediaIngestBatch) {
  const published = batch.successCount;
  if (published > 0) {
    showSuccessToast(
      `Published ${published} item${published === 1 ? '' : 's'}` +
        (batch.failedCount || batch.skippedCount
          ? ` (${batch.failedCount} failed, ${batch.skippedCount} skipped).`
          : '.')
    );
  } else {
    showErrorToast('No items were published. Check per-file results below.');
  }
}

export function MediaLibraryIngestPopup({
  open,
  library,
  onClose,
  onCompleted
}: MediaLibraryIngestPopupProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pollRef = useRef<number | null>(null);
  const openRef = useRef(open);
  const [categories, setCategories] = useState<MediaCategoryAdmin[]>([]);
  const [tags, setTags] = useState<MediaTagAdmin[]>([]);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  const [result, setResult] = useState<MediaIngestBatch | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const accept =
    library === 'gifs'
      ? 'image/gif,.gif'
      : 'image/jpeg,image/png,image/webp,image/avif,.jpg,.jpeg,.png,.webp,.avif';
  const title = library === 'gifs' ? 'Add GIFs' : 'Add memes';

  const activeCategories = useMemo(
    () => categories.filter((category) => category.isActive),
    [categories]
  );
  const activeTags = useMemo(() => tags.filter((tag) => tag.isActive), [tags]);

  const loadMeta = useCallback(async () => {
    try {
      const [categoryRows, tagRows] = await Promise.all([fetchAdminCategories(), fetchAdminTags()]);
      setCategories(categoryRows);
      setTags(tagRows);
      const uncategorized = categoryRows.find((category) => category.slug === 'uncategorized');
      setCategoryId(
        uncategorized?.id ?? categoryRows.find((category) => category.isActive)?.id ?? null
      );
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not load metadata.');
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setPolling(false);
  }, []);

  const finishBatch = useCallback(
    (batch: MediaIngestBatch, dialogOpen: boolean) => {
      setResult(batch);
      setBusy(false);
      setPolling(false);
      stopPolling();
      if (batch.successCount > 0) onCompleted();
      if (dialogOpen) {
        toastBatchResult(batch);
      } else if (batch.successCount > 0) {
        showSuccessToast(
          `Background ingest finished: ${batch.successCount} published` +
            (batch.failedCount || batch.skippedCount
              ? ` (${batch.failedCount} failed, ${batch.skippedCount} skipped).`
              : '.')
        );
      }
    },
    [onCompleted, stopPolling]
  );

  const startPolling = useCallback(
    (batchId: string) => {
      stopPolling();
      setPolling(true);
      setBusy(true);
      pollRef.current = window.setInterval(() => {
        void fetchIngestBatch(batchId)
          .then((batch) => {
            setResult(batch);
            if (isBatchTerminal(batch.state)) finishBatch(batch, openRef.current);
          })
          .catch(() => undefined);
      }, 1500);
    },
    [finishBatch, stopPolling]
  );

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // Keep background polling alive if the dialog is reopened mid-job.
    if (pollRef.current != null) return;
    setFiles([]);
    setResult(null);
    setTagIds([]);
    setBusy(false);
    void loadMeta();
  }, [open, library, loadMeta]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  function addFiles(list: FileList | File[] | null) {
    if (!list) return;
    setFiles((current) => {
      const keys = new Set(current.map((file) => `${file.name}:${file.size}`));
      const merged = [...current];
      for (const file of Array.from(list)) {
        const key = `${file.name}:${file.size}`;
        if (!keys.has(key)) {
          keys.add(key);
          merged.push(file);
        }
      }
      return merged;
    });
  }

  async function onUpload() {
    if (files.length === 0 || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const batch = await createIngestBatch({ library, categoryId, tagIds });
      const done = await uploadIngestFiles(batch.id, files);
      setResult(done);
      if (!isBatchTerminal(done.state)) {
        showSuccessToast(
          `Upload staged (${done.totalCount} files). Processing in the background — you can close this dialog.`
        );
        startPolling(done.id);
        return;
      }
      finishBatch(done, true);
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Ingest failed.');
      setBusy(false);
    }
  }

  const inProgress = busy || polling;

  return (
    <Popup
      visible={open}
      onHiding={onClose}
      title={title}
      showCloseButton
      hideOnOutsideClick={!inProgress}
      width={560}
      height="auto"
      maxHeight="90vh"
    >
      <div className="media-lib-ingest">
        <p className="utility-app__muted media-lib-ingest__hint">
          {library === 'gifs'
            ? 'Only animated GIF files are accepted. Exact duplicates are skipped.'
            : 'Static JPG/PNG/WebP/AVIF only. Animated images are rejected.'}{' '}
          Batches larger than 20 files process in the background after upload.
        </p>

        <div className="media-lib-ingest__fields">
          <label className="utility-app__label">Category</label>
          <SelectBox
            dataSource={activeCategories}
            displayExpr="name"
            valueExpr="id"
            value={categoryId}
            width="100%"
            acceptCustomValue
            searchEnabled
            placeholder="Select or type a new category…"
            onCustomItemCreating={(e) => {
              const name = String(e.text ?? '').trim();
              if (!name) {
                e.customItem = null;
                return;
              }
              const existing = activeCategories.find(
                (category) =>
                  category.name.localeCompare(name, undefined, { sensitivity: 'base' }) === 0
              );
              if (existing) {
                e.customItem = existing;
                return;
              }
              e.customItem = createAdminCategory({ name })
                .then((created) => {
                  setCategories((current) => [...current, created]);
                  return created;
                })
                .catch((err) => {
                  showErrorToast(err instanceof Error ? err.message : 'Could not create category.');
                  return null;
                });
            }}
            onValueChanged={(e) => setCategoryId((e.value as string | null) ?? null)}
          />

          <label className="utility-app__label">Tags</label>
          <TagBox
            dataSource={activeTags}
            displayExpr="name"
            valueExpr="id"
            value={tagIds}
            width="100%"
            acceptCustomValue
            searchEnabled
            showSelectionControls
            applyValueMode="useButtons"
            placeholder="Select or type a new tag…"
            onCustomItemCreating={(e) => {
              const name = String(e.text ?? '').trim();
              if (!name) {
                e.customItem = null;
                return;
              }
              const existing = activeTags.find(
                (tag) => tag.name.localeCompare(name, undefined, { sensitivity: 'base' }) === 0
              );
              if (existing) {
                e.customItem = existing;
                return;
              }
              e.customItem = createMediaTag(name)
                .then((created) => {
                  const row = { ...created, isActive: true, usageCount: 0 };
                  setTags((current) => [...current, row]);
                  return row;
                })
                .catch((err) => {
                  showErrorToast(err instanceof Error ? err.message : 'Could not create tag.');
                  return null;
                });
            }}
            onValueChanged={(e) => setTagIds((e.value as string[]) ?? [])}
          />
        </div>

        <div
          className={`media-lib-ingest__drop${dragOver ? ' media-lib-ingest__drop--active' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e: DragEvent) => {
            e.preventDefault();
            setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
        >
          <p>Drag and drop files here, or</p>
          <Button
            text="Choose files"
            stylingMode="outlined"
            onClick={() => inputRef.current?.click()}
          />
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple
            hidden
            onChange={(e) => {
              addFiles(e.target.files);
              e.currentTarget.value = '';
            }}
          />
        </div>

        {files.length > 0 ? (
          <div className="media-lib-ingest__file-block">
            <div className="media-lib-ingest__file-head">
              <strong>
                {files.length} file{files.length === 1 ? '' : 's'} selected
              </strong>
              <Button
                text="Clear"
                stylingMode="text"
                disabled={inProgress}
                onClick={() => setFiles([])}
              />
            </div>
            <ul className="media-lib-ingest__files">
              {files.map((file) => (
                <li key={`${file.name}:${file.size}`}>
                  {file.name}{' '}
                  <span className="utility-app__muted">({Math.round(file.size / 1024)} KB)</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="utility-app__muted">No files selected yet.</p>
        )}

        <div className="media-lib-ingest__actions">
          <Button
            text={polling ? 'Close (processing continues)' : 'Cancel'}
            stylingMode="outlined"
            onClick={onClose}
          />
          <Button
            text={
              inProgress
                ? polling
                  ? 'Processing…'
                  : `Uploading ${files.length}…`
                : files.length > 0
                  ? `Upload ${files.length} file${files.length === 1 ? '' : 's'}`
                  : 'Upload'
            }
            type="default"
            disabled={inProgress || files.length === 0}
            onClick={() => void onUpload()}
          />
        </div>

        {inProgress ? (
          <p className="utility-app__muted" role="status">
            {polling
              ? 'Background processing — live counts update below. Safe to close this dialog.'
              : 'Uploading and staging files…'}
          </p>
        ) : null}

        {result ? (
          <div className="media-lib-ingest__result">
            <ToolNotice
              kind={
                result.failedCount > 0 ? 'danger' : result.skippedCount > 0 ? 'warning' : 'info'
              }
            >
              Batch {result.state}: {result.successCount} published, {result.skippedCount} skipped,{' '}
              {result.failedCount} failed
              {result.totalCount > 0 ? ` of ${result.totalCount}` : ''}.
            </ToolNotice>
            <ul>
              {result.items.map((item) => (
                <li key={item.id}>
                  <strong>{item.originalFileName}</strong> — {item.state}
                  {item.errorMessage ? `: ${item.errorMessage}` : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Popup>
  );
}
